<template>
    <v-card
        :class="{ 'upload-card': true, 'd-flex': true, 'align-stretch': true }"
        :width="width"
        :height="height"
        elevation="1"
    >
        <!--
        Note: we NEED to use v-show here instead of v-if: the ActionBtn is initialized
        by FlowJs on component-mount, but re-rendered in the process:
        If we use v-if, the re-render re-creates the Button DOM node, which removes it from the flowjs handler.
        We want to keep the dom node here:
    -->
        <div
            v-show="!uploadInProgress"
            ref="dropTarget"
            :class="{
                'inner-border': true,
                'flex-grow-1': true,
                'd-flex': !uploadInProgress, // to allow v-show's display:none, we have to remove the display:flex value when not shown
                'flex-column': true,
                'align-center': true,
                'justify-center': true,
                'drag-over': dragIndicator,
                'pa-5': true,
            }"
            @dragover="dragIndicator = true"
            @dragleave="dragIndicator = false"
            @dragend="dragIndicator = false"
        >
            <KpIcon icon-cls="mediamanager" size="20" color="grey" />
            <ActionBtn ref="uploadBtn" class="ma-1">
                <slot name="upload-action-btn">
                    <KpIcon icon-cls="upload" color="white" disabled-color="grey" />
                    {{ $t('upload_file') }}
                </slot>
            </ActionBtn>
            <span class="ma-1 tgk-paragraph-text">{{ $t('or_drop_here') }}</span>
        </div>

        <div
            :style="{ display: uploadInProgress ? 'flex !important' : 'none !important' }"
            class="ma-2 flex-grow-1 d-flex align-center justify-center"
        >
            <div v-for="(f, i) in flowFiles" :key="f.uniqueIdentifier" class="mb-1 flex-grow-1">
                <v-progress-linear striped height="20" :value="flowFileProgress[i] * 100">
                    {{ f.name }}
                </v-progress-linear>
            </div>
        </div>
    </v-card>
</template>

<script>
/**
 * This component declares a simple card to drop an file. The file then is uploaded
 * to [backend]/your/api/endpoint, and stored.
 *
 * You can pass additional query props using the "uploadParams" object. This info
 * is just added to the upload request.
 *
 * If the upload of (all) file(s) were successful, the 'uploaded' event is
 * fired, with instantiated "file" objects as parameter.
 *
 */
import Flow from '@flowjs/flow.js';
import { errToStr } from '@/helpers/tools';
import { apiUrl } from '@/helpers/globals';
import { getCsrfToken } from '@/helpers/auth';
import { commitToast } from '@/plugins/toast';

export default {
    name: 'UploadCard',
    props: {
        apiEndpoint: {
            type: String,
            required: true,
        },
        uploadParams: {
            type: Object,
            default: () => ({}),
        },
        width: {
            type: String,
            default: '200px',
        },
        height: {
            type: String,
            default: '150px',
        },
        single: {
            type: Boolean,
            default: true,
        },
        allowedTypes: {
            type: String,
            default: 'image/*',
        },
    },
    data() {
        return {
            uploadInProgress: false,
            uploadErrors: [],
            flowFiles: [],
            fileObjs: [],
            flowFileProgress: [],
            dragIndicator: false,
        };
    },
    mounted() {
        // flowjs is initialized on mounting:
        this.initializeFlowUpload();
    },
    methods: {
        /**
         * FlowJS needs to be initialized on component mount. It attaches itself to
         * the DOM and adds the needed event listeners.
         */
        initializeFlowUpload() {
            this.flowObj = new Flow({
                target: apiUrl(this.apiEndpoint),
                chunkSize: 1024 * 500,
                fileParameterName: 'file',
                singleFile: this.single,
                uploadMethod: 'POST',
                testChunks: false,
                simultaneousUploads: 1,
                headers: {
                    'x-csrf-token': getCsrfToken(),
                },
                // query is called for each upload file. It attaches
                // the configured query params:
                query: () => {
                    return this.uploadParams;
                },
            });

            // attach a button and a drop target for adding files:
            this.flowObj.assignBrowse(this.$refs.uploadBtn.$el, false /* isDir */, true /* singleFile */, {
                accept: this.allowedTypes,
            });
            this.flowObj.assignDrop(this.$refs.dropTarget);

            // After files are added, we initialize and start the upload process directly:
            this.flowObj.on('filesSubmitted', (files) => {
                this.flowFileProgress = this.flowFiles.map(() => 0);
                this.uploadInProgress = true;
                this.uploadErrors = [];
                this.fileObjs = [];
                this.flowFiles = [...files];
                this.dragIndicator = false;
                this.flowObj.upload();
            });

            // on progress, we need to update the separate progess array:
            this.flowObj.on('fileProgress', (file) => {
                let idx = this.flowFiles.indexOf(file);
                if (idx >= 0) {
                    // setting a reactive array by index (this.flowFileProgress[idx]) does not work,
                    // the reactive system does not detect it. So we use the VMs $set method here:
                    this.$set(this.flowFileProgress, idx, file.progress());
                }
            });

            /**
             * On each successfully uploaded file
             * object from the server's answer,
             * storing it in this.fileObjs
             */
            this.flowObj.on('fileSuccess', (flowFile, msg) => {
                let fileInfo = JSON.parse(msg);

                if (fileInfo?.files) {
                    fileInfo.files.forEach((file) => {
                        this.fileObjs.push(file);
                    });
                }
            });

            // when the upload(s) is complete, we reset flowjs and update the UI:
            this.flowObj.on('complete', () => {
                this.flowObj.cancel();
                if (this.uploadErrors.length > 0) {
                    this.uploadErrors.forEach((item) => {
                        commitToast({ message: errToStr(item.msg), color: 'error' });
                    });
                }

                this.$emit('uploaded', this.fileObjs, this.flowFiles);
                this.flowFiles = [];
                this.fileObjs = [];
                this.flowFileProgress = [];
                this.uploadInProgress = false;
                this.dragIndicator = false;
            });

            // finally, if errors occur, we collect them and show them in the upload dialog:
            this.flowObj.on('error', (msg, file, chunk) => {
                this.uploadErrors.push({ file: file.name, msg: msg });
                console.error(msg, file, chunk);
            });
        },
    },
};
</script>

<style lang="scss" scoped>
.upload-card {
    background-color: transparent;
    padding: 5px;

    .drag-over {
        background-color: var(--v-primary-base);
        opacity: 0.3;
    }

    .inner-border {
        border: 1px dashed var(--v-primary-base);
        border-radius: 3px;
    }
}
</style>
